import { useCallback, useState } from "react"
import type React from "react"
import { Trans } from "react-i18next"
import { Link, useLocation } from "react-router-dom"
import styles from "./MenuItem.module.css"

interface MenuChild {
  labelKey: string
  icon: string
  route: string
  external: boolean
}

interface MenuItemProps {
  icon: string
  labelKey: string
  route: string
  external?: boolean
  children?: MenuChild[]
}

function MenuLink({ icon, labelKey, route, external }: MenuChild) {
  const location = useLocation()
  const isHome = route === "/"
  const isActive = isHome
    ? location.pathname === "/"
    : location.pathname === route || location.pathname.startsWith(`${route}/`)

  if (external) {
    return (
      <a className={`${styles.Root} ${styles.Sub}${isActive ? ` ${styles.Active}` : ""}`} href={route}>
        <i className={`${styles.Icon} ${icon}`} />
        <span className={styles.Label}>
          <Trans i18nKey={`menu.${labelKey}`} />
        </span>
      </a>
    )
  }

  return (
    <Link className={`${styles.Root} ${styles.Sub}${isActive ? ` ${styles.Active}` : ""}`} to={route}>
      <i className={`${styles.Icon} ${icon}`} />
      <span className={styles.Label}>
        <Trans i18nKey={`menu.${labelKey}`} />
      </span>
    </Link>
  )
}

const MenuItem: React.FC<MenuItemProps> = ({
  icon,
  labelKey,
  route,
  external = false,
  children,
}) => {
  const location = useLocation()
  const [expanded, setExpanded] = useState(
    children?.some((c) => location.pathname === c.route || location.pathname.startsWith(`${c.route}/`)) ?? false,
  )

  const isActive = useCallback(() => {
    const isHome = route === "/"
    return isHome
      ? location.pathname === "/"
      : location.pathname === route || location.pathname.startsWith(`${route}/`)
  }, [location.pathname, route])

  if (children && children.length > 0) {
    const anyChildActive = children.some(
      (c) => location.pathname === c.route || location.pathname.startsWith(`${c.route}/`),
    )

    return (
      <div>
        <button
          className={`${styles.Root} ${styles.Group}${anyChildActive ? ` ${styles.Active}` : ""}`}
          onClick={() => setExpanded((v) => !v)}
          type="button"
        >
          <i className={`${styles.Icon} ${icon}`} />
          <span className={styles.Label}>
            <Trans i18nKey={`menu.${labelKey}`} />
          </span>
          <i
            className={`${styles.Chevron} fa fa-chevron-down`}
            style={{ transform: expanded ? "rotate(180deg)" : "rotate(0deg)" }}
          />
        </button>
        {expanded && (
          <div className={styles.SubMenu}>
            {children.map((child) => (
              <MenuLink key={child.labelKey} {...child} />
            ))}
          </div>
        )}
      </div>
    )
  }

  if (external) {
    return (
      <a className={`${styles.Root}${isActive() ? ` ${styles.Active}` : ""}`} href={route}>
        <i className={`${styles.Icon} ${icon}`} />
        <span className={styles.Label}>
          <Trans i18nKey={`menu.${labelKey}`} />
        </span>
      </a>
    )
  }

  return (
    <Link className={`${styles.Root}${isActive() ? ` ${styles.Active}` : ""}`} to={route}>
      <i className={`${styles.Icon} ${icon}`} />
      <span className={styles.Label}>
        <Trans i18nKey={`menu.${labelKey}`} />
      </span>
    </Link>
  )
}

export default MenuItem
